import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, message, Select, Divider, InputNumber, Space } from 'antd';
import { PostDataAuth } from '../../apiService/PostData';
import { PutDataAuth } from '../../apiService/PutData';
import { apis } from '../../properties';
import useMemberProfilesList from '../../hooks/useMemberProfilesList';
import SingleFileUpload from './SingleFileUpload';

const { Option } = Select;

const CommitteeMemberModal = ({ visible, onCancel, onSubmitSuccess, initialValues }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [isAvatarDirty, setIsAvatarDirty] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    
    const allMembers = useMemberProfilesList(""); 

    useEffect(() => {
        if (visible) {
            if (initialValues) {
                form.setFieldsValue({
                    role_name: initialValues.role_name,
                    display_order: initialValues.display_order,
                    member_id: initialValues.member_id?._id,
                    member_name: initialValues.member_id ? `${initialValues.member_id.first_name} ${initialValues.member_id.last_name}` : '',
                    hash_name: initialValues.member_id?.hash_name,
                });
                setAvatarUrl(initialValues.member_id?.member_avatar || '');
            } else {
                form.resetFields();
                setAvatarUrl('');
            }
            setIsAvatarDirty(false);
        }
    }, [visible, initialValues, form]);

    const handleFinish = async (values) => {
        setLoading(true); 
        try {
            if (isAvatarDirty && values.member_id) {
                await PutDataAuth(`${apis.COMMITTEE_MANAGEMENT}/member/${values.member_id}/avatar`, { 
                    avatar_url: avatarUrl 
                });
            }

            const payload = {
                role_name: values.role_name,
                display_order: values.display_order,
                member_id: values.member_id,
            };

            if (initialValues?._id) {
                await PutDataAuth(`${apis.COMMITTEE_MANAGEMENT}/${initialValues._id}`, payload);
            } else {
                await PostDataAuth(apis.COMMITTEE_MANAGEMENT, payload);
            }

            message.success("Changes saved successfully!");
            if (onSubmitSuccess) onSubmitSuccess();
            onCancel();
        } catch (error) {
            message.error("Failed to save.");
        } finally {
            setLoading(false); 
        }
    };

    return (
        <Modal
            title={initialValues ? "Edit Committee Role" : "Create New Committee Role"}
            open={visible}
            onCancel={onCancel}
            confirmLoading={loading}
            onOk={() => form.submit()}
            width={500}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Space style={{ display: 'flex' }} align="baseline">
                    <Form.Item name="role_name" label="Committee Role" rules={[{ required: true }]} style={{ width: '300px' }}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="display_order" label="Order" rules={[{ required: true }]} style={{ width: '120px' }}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                </Space>

                <Divider style={{ marginTop: 0 }} />

                <SingleFileUpload 
                    url={avatarUrl} 
                    disabled={!form.getFieldValue('member_id')}
                    onUploadSuccess={(newUrl) => {
                        setAvatarUrl(newUrl);
                        setIsAvatarDirty(true);
                    }} 
                />

                <Form.Item name="member_id" label="Select Member Profile" rules={[{ required: true }]}>
                    <Select 
                        showSearch 
                        placeholder="Choose member"
                        onSearch={(value) => setSearchValue(value)}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        filterSort={(optionA, optionB) => {
                            const labelA = (optionA?.label ?? '').toLowerCase();
                            const labelB = (optionB?.label ?? '').toLowerCase();
                            const search = searchValue.toLowerCase();
                            
                            // Prioritize items that start with the search term
                            const aStartsWith = labelA.startsWith(search);
                            const bStartsWith = labelB.startsWith(search);
                            
                            if (aStartsWith && !bStartsWith) return -1;
                            if (!aStartsWith && bStartsWith) return 1;
                            
                            // If both start with or both don't start with, sort alphabetically
                            return labelA.localeCompare(labelB);
                        }}
                        onChange={(val) => {
                            const m = allMembers.find(x => x._id === val);
                            form.setFieldsValue({ member_name: `${m.first_name} ${m.last_name}`, hash_name: m.hash_name });
                            setAvatarUrl(m.member_avatar || '');
                            setIsAvatarDirty(false);
                            setSearchValue('');
                        }}
                    >
                        {allMembers.map(m => (
                            <Option key={m._id} value={m._id} label={`${m.first_name} ${m.last_name}`}>
                                {m.first_name} {m.last_name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <div style={{ display: 'flex', gap: '15px', background: '#fafafa', padding: '10px', borderRadius: '8px' }}>
                    <Form.Item name="member_name" label="Member Name" style={{ flex: 1 }}><Input disabled variant="borderless" /></Form.Item>
                    <Form.Item name="hash_name" label="Hash Name" style={{ flex: 1 }}><Input disabled variant="borderless" /></Form.Item>
                </div>
            </Form>
        </Modal>
    );
};

export default CommitteeMemberModal;